import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Search, Plus, Phone, Instagram, User, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

import { Outlet } from "@tanstack/react-router";

export const Route = createFileRoute("/clientes")({
  component: () => <Outlet />,
});

// Tipagem para Clientes (Mock por enquanto já que Supabase não habilitou)
interface Cliente {
  id: string;
  nome: string;
  telefone: string;
  email: string;
  instagram: string;
  data_nascimento: string;
  observacoes: string;
  total_gasto: number;
  status: "Ativo" | "Inativo";
  created_at: string;
  ultimo_agendamento?: string;
}

const MOCK_CLIENTES: Cliente[] = [
  {
    id: "1",
    nome: "Carlos Eduardo",
    telefone: "(11) 98888-7777",
    email: "carlos@email.com",
    instagram: "cadu_tattoo",
    data_nascimento: "1990-05-15",
    observacoes: "Alergia a tinta vermelha.",
    total_gasto: 1200.00,
    status: "Ativo",
    created_at: new Date().toISOString(),
    ultimo_agendamento: new Date().toISOString(),
  },
  {
    id: "2",
    nome: "Mariana Silva",
    telefone: "(11) 97777-6666",
    email: "mari@email.com",
    instagram: "mari_silva",
    data_nascimento: "1995-10-20",
    observacoes: "",
    total_gasto: 450.00,
    status: "Inativo",
    created_at: new Date(Date.now() - 100 * 24 * 60 * 60 * 1000).toISOString(),
    ultimo_agendamento: new Date(Date.now() - 100 * 24 * 60 * 60 * 1000).toISOString(),
  }
];

function ClientesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [clientes, setClientes] = useState<Cliente[]>(MOCK_CLIENTES);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);

  // Form State
  const [formData, setFormData] = useState({
    nome: "",
    telefone: "",
    email: "",
    instagram: "",
    data_nascimento: "",
    observacoes: ""
  });

  const filteredClientes = useMemo(() => {
    return clientes
      .filter(c => 
        c.nome.toLowerCase().includes(search.toLowerCase()) || 
        c.telefone.includes(search)
      )
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [search, clientes]);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nome || !formData.telefone) {
      toast.error("Nome e Telefone são obrigatórios!");
      return;
    }

    if (selectedCliente) {
      setClientes(prev => prev.map(c => c.id === selectedCliente.id ? { ...c, ...formData } : c));
      toast.success("Cliente atualizado com sucesso!");
    } else {
      const newCliente: Cliente = {
        ...formData,
        id: Math.random().toString(36).substr(2, 9),
        total_gasto: 0,
        status: "Ativo",
        created_at: new Date().toISOString(),
        instagram: formData.instagram ? `@${formData.instagram.replace('@', '')}` : ''
      };
      setClientes(prev => [newCliente, ...prev]);
      toast.success("Cliente cadastrado com sucesso!");
    }
    
    setIsDialogOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      nome: "",
      telefone: "",
      email: "",
      instagram: "",
      data_nascimento: "",
      observacoes: ""
    });
    setSelectedCliente(null);
  };

  const openEdit = (cliente: Cliente, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCliente(cliente);
    setFormData({
      nome: cliente.nome,
      telefone: cliente.telefone,
      email: cliente.email,
      instagram: cliente.instagram.replace('@', ''),
      data_nascimento: cliente.data_nascimento,
      observacoes: cliente.observacoes
    });
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-foreground">Clientes</h1>
          <p className="text-muted-foreground">Gerencie sua base de clientes</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 text-white w-full md:w-auto">
              <Plus className="mr-2 size-4" />
              Novo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border text-foreground max-w-lg">
            <DialogHeader>
              <DialogTitle>{selectedCliente ? "Editar Cliente" : "Cadastrar Novo Cliente"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome Completo *</Label>
                  <Input 
                    id="nome" 
                    value={formData.nome} 
                    onChange={e => setFormData({...formData, nome: e.target.value})}
                    placeholder="Ex: João Silva"
                    className="bg-background border-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone (DDD) *</Label>
                  <Input 
                    id="telefone" 
                    value={formData.telefone} 
                    onChange={e => setFormData({...formData, telefone: e.target.value})}
                    placeholder="(00) 00000-0000"
                    className="bg-background border-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input 
                    id="email" 
                    type="email"
                    value={formData.email} 
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    placeholder="email@exemplo.com"
                    className="bg-background border-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instagram">Instagram</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
                    <Input 
                      id="instagram" 
                      value={formData.instagram} 
                      onChange={e => setFormData({...formData, instagram: e.target.value})}
                      placeholder="usuario"
                      className="bg-background border-border pl-8"
                    />
                  </div>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="nascimento">Data de Nascimento</Label>
                  <DatePicker
                    id="nascimento"
                    value={formData.data_nascimento}
                    onChange={v => setFormData({...formData, data_nascimento: v})}
                    comSeletorDeAno
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="obs">Observações</Label>
                  <Textarea 
                    id="obs" 
                    value={formData.observacoes} 
                    onChange={e => setFormData({...formData, observacoes: e.target.value})}
                    placeholder="Alergias, preferências, estilos favoritos..."
                    className="bg-background border-border min-h-[100px]"
                  />
                </div>
              </div>
              <DialogFooter className="pt-4">
                <Button type="submit" className="bg-primary hover:bg-primary/90 text-white w-full">
                  Salvar Cliente
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input 
          placeholder="Buscar por nome ou telefone..." 
          className="pl-10 bg-card border-border"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredClientes.map((cliente) => (
          <Card 
            key={cliente.id} 
            className="bg-card border-border hover:border-primary/50 transition-all cursor-pointer group"
            onClick={() => navigate({ to: `/clientes/${cliente.id}` })}
          >
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="bg-primary/10 p-3 rounded-xl text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                  <User size={24} />
                </div>
                <Badge className={cliente.status === "Ativo" ? "bg-green-500/10 text-green-500 border-green-500/20" : "bg-zinc-500/10 text-muted-foreground border-zinc-500/20"}>
                  {cliente.status}
                </Badge>
              </div>
              
              <div className="space-y-1">
                <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors">{cliente.nome}</h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Phone size={14} />
                  {cliente.telefone}
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-border flex justify-between items-center">
                <span className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Total Gasto</span>
                <span className="text-primary font-semibold text-lg">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cliente.total_gasto)}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredClientes.length === 0 && (
        <div className="text-center py-20 border-2 border-dashed border-border rounded-xl">
          <User className="mx-auto size-12 text-muted-foreground mb-4 opacity-20" />
          <p className="text-muted-foreground">Nenhum cliente encontrado.</p>
        </div>
      )}
    </div>
  );
}
